import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormLabel,
  Tooltip,
  Button,
  Autocomplete,
} from "@mui/material";
import { YesOrNo } from "../../../constants/YesOrNo";
import InfoIcon from "@mui/icons-material/Info";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { textStyle } from "../../styles/Style";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const GolfCourses = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [courseName, setCourseName] = useState<string | null>(null);
  const [durationOf9Holes, setDurationOf9Holes] = useState<Number | null>(null);
  const [durationOf18Holes, setDurationOf18Holes] = useState<Number | null>(
    null,
  );
  const [outletList, setOutletList] = useState<any>([]);
  const [defaultOutlet, setDefaultOutlet] = useState<Number | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [GolfCourses, setGolfCourses] = useState<any>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateInput = () => {
    if (!courseName) {
      setAlertMessage("Course name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (!defaultOutlet) {
      setAlertMessage("Default outlet is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    if (
      courseName &&
      !validateString(
        courseName,
        validationLength.courseName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  // useEffect(() => {
  //   validateInput();
  // }, [courseName, defaultOutlet]);

  useEffect(() => {
    if (fieldValues) {
      setGolfCourses(fieldValues[GolfPropertyEnum.courses]);
      setOutletList(
        fillAutocompleteValues(
          fieldValues[GolfPropertyEnum.outletSetup],
          DNDFieldsEnum.OUTLETNAME,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(GolfCourses);
    editClicked &&
      setSelectedId(checkData(GolfCourses, selectedId) ? selectedId : null);
  }, [GolfCourses]);

  const updateGolfCourses = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (GolfCourses ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.COURSENAME,
        courseName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setGolfCourses((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editGolfCourses(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${GolfPropertyEnum.courses} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfCourses = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(GolfCourses ?? [], DNDFieldsEnum.COURSENAME, courseName)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfCourses((prev: any) => {
      return updateData(
        prev?.length
          ? [
              ...prev,
              {
                id: (prev?.length ?? 0) + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfCourses(null);
    setEditClicked(false);
  };

  const editGolfCourses = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setCourseName(list?.["Course Name"]);
    setDefaultOutlet(list?.["Default Outlet"]);
    setDurationOf9Holes(list?.["Duration of Play 9 Holes"]);
    setDurationOf18Holes(list?.["Duration of Play 18 Holes"]);
  };

  const deleteGolfCourses = (id: number) => {
    setGolfCourses(deleteItem(GolfCourses ?? [], id));
  };

  const payload = {
    "Course Name": courseName,
    "Default Outlet": defaultOutlet,
    "Duration of Play 9 Holes": durationOf9Holes,
    "Duration of Play 18 Holes": durationOf18Holes,
  };

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            Courses
            <Tooltip title={tooltipComponent(golfTooltipHeaders.courseHeader)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editGolfCourses(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editGolfCourses(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Course Name
            <Tooltip title={tooltipComponent(golfTooltipHeaders.courseName)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="courseName"
            variant="outlined"
            placeholder="Course Name"
            value={courseName ?? ""}
            helperText={nameError ? validationMessages.courseName : ""}
            error={nameError}
            onChange={(e: any) => {
              setCourseName(e.target.value);
            }}
          />
        </Stack>
        <Stack direction="row" width="100%" gap="5px">
          <Autocomplete
            id="outletList"
            disablePortal
            options={removeSampleTestArray(outletList)}
            sx={{ width: "100%" }}
            value={defaultOutlet?.toString() ?? ""}
            disabled={!outletList}
            onChange={(e: any) => setDefaultOutlet(e.target.innerText)}
            renderInput={(params) => <TextField {...params} label="Outlet" />}
          />
          <Tooltip title={tooltipComponent(golfTooltipHeaders.defaultOutlet)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack>
          <FormLabel component="legend">
            Duration of Play 9 Holes
            <Tooltip title={tooltipComponent(golfTooltipHeaders.hole9)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="durationOf9Holes"
            variant="outlined"
            placeholder="Duration of Play 9 holes(minutes)"
            value={durationOf9Holes ?? ""}
            type="number"
            onChange={(e: any) => {
              setDurationOf9Holes(e.target.value);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Duration of Play 18 Holes
            <Tooltip title={tooltipComponent(golfTooltipHeaders.hole18)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="durationOf18Holes"
            variant="outlined"
            placeholder="Duration of Play 18 holes(minutes)"
            value={durationOf18Holes ?? ""}
            type="number"
            onChange={(e: any) => {
              setDurationOf18Holes(e.target.value);
            }}
          />
        </Stack>
        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() =>
              selectedId ? updateGolfCourses() : addGolfCourses()
            }
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Course
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Courses Available</Typography>
          <Stack direction="column" gap="10px">
            {GolfCourses ? (
              <DraggableItemList
                itemData={GolfCourses}
                onOrderUpdate={(order) => setGolfCourses(order)}
                onEdit={(info) => editGolfCourses(info)}
                onDelete={(id) => deleteGolfCourses(id)}
                code={DNDFieldsEnum.COURSENAME}
                name={DNDFieldsEnum.DEFAULTOUTLET}
              />
            ) : (
              <Typography variant="body2">
                No Course Information Available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};
export default GolfCourses;
